import React, { useState } from "react";
import {
  toWSDateString,
  useModalOldContext,
  WSButtonProps,
  WSCard,
  WSElementProps,
  WSEmptyState,
  WSGrid,
  WSList,
  WSLoader,
  WSSectionToolbar,
  WSTable,
  WSTableCell,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  useMemberPrivateFiles,
  useMemberPublicFiles
} from "../../../query/files/queries";
import {
  IMemberFileResponse,
  IMemberPublicFileResponse
} from "@wingspanhq/files/dist/lib/interfaces";
import { format } from "../../../utils/dates";
import { filesService } from "../../../services/files";
import {
  DeleteFileModal,
  DOCUMENTS_MODALS_DELETE_FILE
} from "../../../Documents/modals/DeleteFileModal";
import { RequestTemplateModal } from "../../../Documents/components/RequestTemplateModal/RequestTemplateModal";
import { openInNewTab } from "../../../utils/openInNewTab";
import { useHistory } from "react-router-dom";
import templates from "../../../Documents/templates.json";

interface RecommendedTemplatesProps extends WSElementProps {}

interface TemplateCardProps extends WSElementProps {
  title: string;
  link: string;
  type?: string;
  footer?(link: string): React.ReactNode;
}
export const FileCard: React.FC<TemplateCardProps> = ({
  title,
  link,
  type = "Google Doc"
}) => {
  const actionConfig: WSButtonProps<"Link"> = ["Google Doc", "Form"].includes(
    type
  )
    ? {
        label: "View",
        rightIcon: "open-tab",
        onClick: () => openInNewTab(link)
      }
    : {
        label: "Download",
        icon: "download",
        onClick: () => openInNewTab(link)
      };

  return (
    <WSCard
      header={{
        label: title,
        value: {
          action: actionConfig
        }
      }}
    >
      <WSText.ParagraphSm color="gray600">{type}</WSText.ParagraphSm>
    </WSCard>
  );
};

export const RecommendedTemplates: React.FC<
  RecommendedTemplatesProps
> = props => {
  const history = useHistory();
  const onClickViewAll = () => {
    history.push("/member/knowledge-center");
  };

  const recommendedTemplates = templates.slice(0, 4);
  return (
    <WSList gap="L">
      <WSSectionToolbar
        title="Recommended templates"
        buttons={[
          {
            kind: "Link",
            label: "View all",
            onClick: onClickViewAll
          }
        ]}
      />

      <WSList gap="M">
        {recommendedTemplates.map(template => (
          <FileCard key={template.title} {...template} />
        ))}
      </WSList>
    </WSList>
  );
};

type TableData = {
  source: IMemberFileResponse | IMemberPublicFileResponse;
  name: string;
  createdAt: Date;
};

export const GeneralDocuments: React.FC<{}> = () => {
  const { openModal } = useModalOldContext();

  const { data: dataFilesPublic = [], isLoading: isLoadingFilesPublic } =
    useMemberPublicFiles();

  const { data: dataFilesPrivate = [], isLoading: isLoadingFilesPrivate } =
    useMemberPrivateFiles();

  const files: (IMemberPublicFileResponse | IMemberFileResponse)[] = [
    ...dataFilesPrivate,
    ...dataFilesPublic
  ].map((file: IMemberPublicFileResponse | IMemberFileResponse) => ({
    ...file,
    createdAtDisplayValue: format(file.createdAt)
  }));

  const [requestTemplateModalVisible, setRequestTemplateModalVisible] =
    useState(false);

  const downloadFileFromTable = async (
    row: IMemberFileResponse | IMemberPublicFileResponse
  ) => {
    try {
      let data;
      if (row["fileAccessScope"] === "Private") {
        data = await filesService.member.private.download(row.fileId);
      } else {
        data = await filesService.member.public.download(row.fileId);
      }
      const tempLink = document.createElement("a");
      tempLink.href = window.URL.createObjectURL(
        new Blob([data], { type: row.mimetype ? row.mimetype : "" })
      );
      tempLink.setAttribute("download", row.filename);
      const tempElement = document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempElement);
    } catch {
      //download failed, do nothing
    }
  };

  const tableData: WSTableItem<TableData>[] = files.map(file => {
    return {
      data: {
        source: file,
        name: file.filename,
        createdAt: file.createdAt
      },
      id: file.fileId
    };
  });

  const modals = (
    <>
      <DeleteFileModal />
      {requestTemplateModalVisible && (
        <RequestTemplateModal
          onSubmit={() => {
            setRequestTemplateModalVisible(false);
          }}
          onClose={() => {
            setRequestTemplateModalVisible(false);
          }}
        />
      )}
    </>
  );

  return (
    <>
      {modals}
      <WSGrid>
        <WSGrid.Item span={{ s: "12", m: "8" }}>
          {isLoadingFilesPublic || isLoadingFilesPrivate ? (
            <WSLoader.Spinner my="3XL" />
          ) : files.length === 0 ? (
            <WSEmptyState
              title="No files yet"
              type="documents"
              description="Everything you generate in Wingspan will automatically be stored
              here for your reference."
            />
          ) : (
            <WSTable<TableData>
              mt="XL"
              showHeader={true}
              rowMenuActions={rowItem => [
                {
                  label: "Download",
                  icon: "download",
                  onClick: () => downloadFileFromTable(rowItem.data.source)
                },
                {
                  label: "Delete",
                  icon: "trash",
                  onClick: () =>
                    openModal(DOCUMENTS_MODALS_DELETE_FILE, {
                      source: rowItem.data.source as
                        | IMemberFileResponse
                        | IMemberPublicFileResponse
                    })
                }
              ]}
              columns={[
                {
                  config: {
                    header: {
                      text: "Name"
                    },
                    gridTemplateSizeMax: "1fr"
                  },
                  renderFunction: row => <WSTableCell text={row.data.name} />
                },
                {
                  config: {
                    header: {
                      text: "Created"
                    },
                    justify: "start",
                    gridTemplateSizeMax: "1fr"
                  },
                  renderFunction: row => (
                    <WSTableCell text={toWSDateString(row.data.createdAt)} />
                  )
                }
              ]}
              tableData={tableData}
            />
          )}
        </WSGrid.Item>
        <WSGrid.Item span={{ s: "12", m: "4" }}>
          <RecommendedTemplates mb="2XL" />
        </WSGrid.Item>
      </WSGrid>
    </>
  );
};
